import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setAiScannerAnswer } from '../../actions/scan';
import { COUNT_PLACEHOLDER } from '../../constants/aiScanner';
import INVESTMENT_QUESTIONS from '../../constants/investmentQuestions';
import warningIcon from '../../icons/Icon-Yellow-Alert.svg';
import useLocalize from '../../utils/hooks/useLocalize';
import { renderCountChunk } from './aiScannerChunks';

const NO_ANSWERS = { q1: null, q2: null };
const TAG_SEPARATOR = ' / ';

/**
 * Renders the quoted hashtag segment of the header in semibold, as a `<b>`
 * rich-text chunk (see `AiScannerCoachMark` for the same pattern).
 */
const renderEmphasisChunk = (chunks) => (
  <span className="ai-scanner-message-emphasis">{chunks}</span>
);

/**
 * InvestmentQuestions Component
 *
 * STORY-009: after the member taps 「NNN 件を表示」 in the hashtag sheet, this
 * renders the AI Scanner's opening message, the intro line, and the two
 * investment questions as sequential message blocks.
 *
 * Q2 is revealed only once Q1 has been answered — before that no Q2 markup
 * exists in the DOM at all. Answers live in `state.scans.aiScannerAnswers`, so
 * this component owns no answer state of its own; STORY-010 picks the pair up
 * from the store (or from `onAnswersComplete`) to run scoring.
 *
 * At `matchCount === 0` the step reduces to the condition line, the count line,
 * and a warning — the intro and both questions are dropped, since there are no
 * candidates left to narrow. Reaching it requires reopening the sheet mid-flow
 * — the sheet's submit button is disabled at 0, so the step cannot be entered
 * this way.
 *
 * #737 (AC C20108249) splits the two ways of arriving at that zero, which
 * STORY-009 treated alike. A selection that MATCHED nothing keeps the
 * condition and count lines, which still say which selection produced the
 * zero; a selection that was CLEARED ENTIRELY drops them, because
 * 「「」の条件で探しました✨」 above 「0 件 見つかりましたね！」 describes a search
 * the member has just taken back. There the warning is all that renders.
 *
 * Answers are not cleared, so loosening the selection back to one or more
 * matches restores both questions with their previous selections intact.
 *
 * Scoring, the candidate count, the AI summary, the result cards, and the
 * confirmation message are all out of scope here.
 *
 * @param {Object} props
 * @param {string[]} [props.selectedTags] - Already-localized hashtag labels in
 *   selection order. Joined with " / " for the header message (FR-001).
 * @param {number|null} [props.matchCount] - STORY-008 match count. `null`
 *   renders the same "-" placeholder the hashtag sheet uses. `0` swaps both
 *   questions for the no-results warning (bypassed when `readOnly`).
 * @param {Function} [props.onAnswersComplete] - Called with `{ q1, q2 }` each
 *   time a selection leaves both questions answered, including the re-emit when
 *   Q1 changes after Q2 was answered (Rule 4). Never called when `readOnly`.
 * @param {boolean} [props.readOnly] - STORY (#725): renders a persisted
 *   `SearchRound`'s answers from `overrideAnswers` instead of the live
 *   `state.scans.aiScannerAnswers`, with both questions always shown
 *   (bypassing the zero-match warning, since a persisted round's questions
 *   were always answered to completion) and no interaction wired up —
 *   tapping an option dispatches nothing and never calls `onAnswersComplete`.
 * @param {{q1: string|null, q2: string|null}} [props.overrideAnswers] - The
 *   answers to render when `readOnly` is `true`.
 * @param {string} [props.instanceId] - Namespace for the radio group `name`s
 *   and prompt `id`s. A resumed session mounts one of these per round, and a
 *   radio group is scoped to `name` across the whole document — without a
 *   distinct namespace the last round rendered steals the checked state from
 *   every earlier one.
 * @param {React.ReactNode} [props.prompt] - STORY-015 (#729): an extra line
 *   rendered inside the header block, below the count and in place of the
 *   intro — where the re-search question 「…前回と同じ条件で進めますか？」 sits.
 *   Taken as a node rather than a string key so the caller keeps ownership of
 *   its `id`, which its own options reference via `aria-labelledby`.
 * @param {boolean} [props.hideHeader] - Drops the condition/count/intro block
 *   entirely. STORY-015 (#729) Scenario B re-asks Q1/Q2 directly under the
 *   re-search prompt, which has already headed that block, and repeating it
 *   would claim the search ran twice.
 * @param {React.ReactNode} [props.children] - Rendered after the questions.
 * @returns {JSX.Element}
 */
export const InvestmentQuestions = ({
  selectedTags = [],
  matchCount = null,
  onAnswersComplete,
  readOnly = false,
  overrideAnswers,
  instanceId = 'ai-scanner',
  prompt = null,
  hideHeader = false,
  children = null
}) => {
  const localize = useLocalize();
  const dispatch = useDispatch();
  const liveAnswers = useSelector(
    (state) => (state && state.scans && state.scans.aiScannerAnswers) || NO_ANSWERS
  );
  const answers = readOnly ? overrideAnswers || NO_ANSWERS : liveAnswers;

  const handleSelect = useCallback(
    (questionId, code) => {
      // A restored round's questions are frozen — no dispatch, no hand-off.
      if (readOnly) return;
      // Rapid repeat taps on the current answer must not re-dispatch or
      // re-fire the hand-off. A native radio suppresses the change event on
      // its own, so this only guards programmatic callers.
      if (answers[questionId] === code) return;

      dispatch(setAiScannerAnswer(questionId, code));

      const next = Object.assign({}, answers, { [questionId]: code });
      if (next.q1 !== null && next.q2 !== null && onAnswersComplete) {
        onAnswersComplete(next);
      }
    },
    [answers, dispatch, onAnswersComplete, readOnly]
  );

  // A selection that matches nothing cannot be narrowed further, so the
  // questions are replaced by a warning until the member loosens it again.
  // Tests `=== 0` deliberately: a `null` count is loading or error, which
  // keeps the questions up alongside the "-" placeholder rather than
  // claiming there are no matches. Bypassed entirely when `readOnly` — a
  // persisted round's questions were always answered to completion, so the
  // live "matches nothing" warning can never apply to it.
  const hasNoResults = !readOnly && matchCount === 0;

  // #737 AC C20108249: the member reopened the sheet, cleared every hashtag
  // and closed it again, so there is no longer a selection to report. The
  // condition line would read 「「」の条件で探しました✨」 and the count line
  // 「0 件 見つかりましたね！」 — a cheerful confirmation of a search that no
  // longer exists — so both go and the warning is left on its own.
  //
  // Keyed on the tags rather than on `hasNoResults` alone: a NON-empty
  // selection that genuinely matches nothing keeps both lines, because there
  // they are still factual and they are the only thing telling the member
  // which selection produced the zero.
  const hasNoSelection = hasNoResults && selectedTags.length === 0;

  const visibleQuestions =
    !readOnly && answers.q1 === null
      ? INVESTMENT_QUESTIONS.slice(0, 1)
      : INVESTMENT_QUESTIONS;

  return (
    <div className="ai-scanner-questions">
      {/*
        #737 AC C20108249: the whole message block goes once the selection has
        been cleared, leaving the warning below as the only thing on screen.
        Dropped as a unit rather than line by line — with the condition, the
        count and the intro all suppressed there is nothing left in it, and an
        empty block would still claim a 32px gap in `.ai-scanner-questions`.
      */}
      {!hasNoSelection && !hideHeader && (
        <div className="ai-scanner-message">
          <p>
            {localize('ai.scanner.questions.header.condition', {
              tags: selectedTags.join(TAG_SEPARATOR),
              b: renderEmphasisChunk
            })}
          </p>
          <p>
            {localize('ai.scanner.questions.header.count', {
              count:
                matchCount === null ? COUNT_PLACEHOLDER : String(matchCount),
              b: renderCountChunk
            })}
          </p>
          {/*
            Dropped at zero matches: the intro promises two questions that are
            not rendered. The condition and count lines above it stay — they
            are still factual, and they show the member which selection
            produced zero. Also dropped when `readOnly` (STORY #725): a
            restored round's radios are already answered, frozen, and
            non-interactive, so "answer the next two questions" is misleading
            above them.
          */}
          {!hasNoResults && !readOnly && !prompt && (
            <p>{localize('ai.scanner.questions.intro')}</p>
          )}
          {prompt}
        </div>
      )}
      {/*
        Always mounted. A live region inserted at the same moment as its text
        is announced unreliably — VoiceOver in particular ignores it — so the
        region has to be in the accessibility tree before there is anything to
        say. While idle it is empty and absolutely positioned, so it paints
        nothing and contributes no flex gap.
      */}
      <p
        className={classNames('ai-scanner-questions-warning', {
          'ai-scanner-questions-warning--idle': !hasNoResults
        })}
        role="status"
      >
        {hasNoResults && (
          <>
            <img
              className="ai-scanner-questions-warning-icon"
              src={warningIcon}
              alt=""
              aria-hidden="true"
              width="20"
              height="20"
            />
            <span>{localize('ai.scanner.questions.no.results')}</span>
          </>
        )}
      </p>
      {!hasNoResults && visibleQuestions.map((question) => {
        const promptId = `${instanceId}-${question.id}-prompt`;
        // Figma 5271:13005: a restored round offers no choice, so it shows
        // only the answer that was given. A round persisted before a question
        // was answered has nothing to show, and keeps the full list rather
        // than an empty box.
        const answered = question.options.filter(
          (option) => answers[question.id] === option.code
        );
        const options =
          readOnly && answered.length ? answered : question.options;
        return (
          <div
            className={classNames('ai-scanner-question', {
              'ai-scanner-question--readonly': readOnly
            })}
            key={question.id}
          >
            <p className="ai-scanner-question-prompt" id={promptId}>
              {localize(question.promptKey)}
            </p>
            <fieldset
              className="ai-scanner-question-options"
              role="radiogroup"
              aria-labelledby={promptId}
            >
              {options.map((option) => {
                const isSelected = answers[question.id] === option.code;
                return (
                  <label
                    key={option.code}
                    className={classNames('ai-scanner-question-option', {
                      'ai-scanner-question-option--selected': isSelected,
                      'ai-scanner-question-option--readonly': readOnly
                    })}
                  >
                    <input
                      type="radio"
                      className="ai-scanner-question-option-input"
                      name={`${instanceId}-${question.id}`}
                      value={option.code}
                      checked={isSelected}
                      disabled={readOnly}
                      onChange={() => handleSelect(question.id, option.code)}
                    />
                    <span
                      className="ai-scanner-question-option-mark"
                      aria-hidden="true"
                    />
                    <span className="ai-scanner-question-option-label">
                      {localize(option.labelKey)}
                    </span>
                  </label>
                );
              })}
            </fieldset>
            {question.id === 'q2' && question.cautionKey && (
              <p className="ai-scanner-question-caution">
                {localize(question.cautionKey)}
              </p>
            )}
          </div>
        );
      })}
      {children}
    </div>
  );
};

InvestmentQuestions.propTypes = {
  selectedTags: PropTypes.arrayOf(PropTypes.string),
  matchCount: PropTypes.number,
  onAnswersComplete: PropTypes.func,
  readOnly: PropTypes.bool,
  overrideAnswers: PropTypes.shape({
    q1: PropTypes.string,
    q2: PropTypes.string
  }),
  instanceId: PropTypes.string,
  prompt: PropTypes.node,
  hideHeader: PropTypes.bool,
  children: PropTypes.node
};

export default InvestmentQuestions;
